import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Layers, Play, Square, PenLine } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/features/auth';
import { useCaseInstances, useCaseInstanceMutations } from '../hooks';
import type { CaseInstance, Matter } from '../types';

interface CaseInstancesCardProps {
  matter: Matter;
}

type DialogMode = 'start' | 'stop' | 'edit' | null;

/**
 * Case Instances — named work engagements on an INSTANCE-billed matter
 * (`contract.periodType === 'INSTANCE'`). Lists instances (active first), lets a
 * case member / Partner / MD START a new instance (name), STOP the active one
 * (recording a result), and edit any instance's name/result. Gated on
 * `matter:case-instance`.
 */
export function CaseInstancesCard({ matter }: CaseInstancesCardProps) {
  const { t } = useTranslation();
  const { hasAccess } = useAuth();

  // Gated on a court case whose contract is INSTANCE-billed.
  const isEnabled =
    !!matter?.isCourtCase && matter?.contract?.periodType === 'INSTANCE';
  const { data: instances, isLoading } = useCaseInstances(
    matter?.id,
    isEnabled,
  );
  const {
    startCaseInstanceMutation,
    stopCaseInstanceMutation,
    updateCaseInstanceMutation,
  } = useCaseInstanceMutations();

  const canManage =
    hasAccess('matter', 'case-instance') && matter?.phase !== 'CLOSED';

  const active = (instances ?? []).find((i) => !i.stoppedAt) ?? null;

  const [mode, setMode] = React.useState<DialogMode>(null);
  const [editing, setEditing] = React.useState<CaseInstance | null>(null);
  const [name, setName] = React.useState('');
  const [result, setResult] = React.useState('');

  if (!isEnabled) return null;

  const openStart = () => {
    setEditing(null);
    setName('');
    setMode('start');
  };
  const openStop = () => {
    setEditing(active);
    setResult('');
    setMode('stop');
  };
  const openEdit = (instance: CaseInstance) => {
    setEditing(instance);
    setName(instance?.name ?? '');
    setResult(instance?.result ?? '');
    setMode('edit');
  };
  const close = () => setMode(null);

  const isPending =
    startCaseInstanceMutation.isPending ||
    stopCaseInstanceMutation.isPending ||
    updateCaseInstanceMutation.isPending;

  const handleSubmit = async () => {
    if (!matter || isPending) return;
    if (mode === 'start') {
      if (!name.trim()) return;
      await startCaseInstanceMutation.mutateAsync({
        id: matter.id,
        data: { name: name.trim() },
      });
    } else if (mode === 'stop') {
      if (!result.trim()) return;
      await stopCaseInstanceMutation.mutateAsync({
        id: matter.id,
        data: { result: result.trim() },
      });
    } else if (mode === 'edit' && editing) {
      await updateCaseInstanceMutation.mutateAsync({
        id: matter.id,
        instanceId: editing.id,
        data: { name: name.trim(), result: result.trim() },
      });
    }
    close();
  };

  const submitDisabled =
    isPending ||
    (mode === 'start' && !name.trim()) ||
    (mode === 'stop' && !result.trim()) ||
    (mode === 'edit' && !name.trim());

  return (
    <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-slate-400" />
            {t('caseInstance.cardTitle')}
          </span>
          {canManage &&
            (active ? (
              <Button size="sm" variant="outline" onClick={openStop}>
                <Square className="mr-1 h-4 w-4" />
                {t('caseInstance.stop')}
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={openStart}>
                <Play className="mr-1 h-4 w-4" />
                {t('caseInstance.start')}
              </Button>
            ))}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoading ? (
          <p className="text-sm text-slate-500">{t('loading')}</p>
        ) : !instances?.length ? (
          <p className="text-sm text-slate-500">{t('caseInstance.empty')}</p>
        ) : (
          <ul className="space-y-3 max-h-72 overflow-y-auto pr-1">
            {instances.map((instance) => {
              const running = !instance?.stoppedAt;
              return (
                <li
                  key={instance.id}
                  className={`rounded-md border px-3 py-2 text-sm ${
                    running
                      ? 'border-emerald-200 bg-emerald-50/50'
                      : 'border-slate-200 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 font-medium text-slate-800">
                      {instance?.name}
                      {running && (
                        <Badge className="border border-emerald-300 bg-emerald-50 text-emerald-700">
                          {t('caseInstance.running')}
                        </Badge>
                      )}
                    </span>
                    {canManage && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(instance)}
                      >
                        <PenLine className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                  <div className="mt-1 space-y-0.5 text-xs text-slate-500">
                    <p>
                      {t('caseInstance.startedLabel')}:{' '}
                      {format(new Date(instance.startedAt), 'dd MMM yyyy HH:mm')}
                      {instance?.stoppedAt
                        ? ` · ${t('caseInstance.stoppedLabel')}: ${format(
                            new Date(instance.stoppedAt),
                            'dd MMM yyyy HH:mm',
                          )}`
                        : ''}
                    </p>
                    {instance?.result && (
                      <p className="text-slate-600">
                        {t('caseInstance.resultLabel')}: {instance.result}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      <Dialog open={mode !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-slate-500" />
              {mode === 'start'
                ? t('caseInstance.startDialogTitle')
                : mode === 'stop'
                  ? t('caseInstance.stopDialogTitle')
                  : t('caseInstance.editDialogTitle')}
            </DialogTitle>
            <DialogDescription>
              {mode === 'stop'
                ? t('caseInstance.stopDialogDescription')
                : t('caseInstance.dialogDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="my-2 space-y-4">
            {/* Name — set on start and editable; hidden on stop */}
            {mode !== 'stop' && (
              <div>
                <label className="mb-2 block text-sm font-medium">
                  {t('caseInstance.nameLabel')}
                </label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('caseInstance.namePlaceholder')}
                  maxLength={255}
                />
              </div>
            )}

            {/* Result — required on stop, editable on edit */}
            {mode !== 'start' && (
              <div>
                <label className="mb-2 block text-sm font-medium">
                  {t('caseInstance.resultLabel')}
                </label>
                <Textarea
                  value={result}
                  onChange={(e) => setResult(e.target.value)}
                  placeholder={t('caseInstance.resultPlaceholder')}
                  rows={3}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={isPending}>
              {t('cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={submitDisabled}>
              {isPending ? t('processing') : t('caseInstance.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
